import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search as SearchIcon } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, HsId, PageHeader, Section } from "@/components/kit";
import { SettlementRow, TxnRow } from "@/components/Rows";
import { CATEGORY_LABEL } from "@/lib/format";

export default function Search() {
  const { gid } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState(sp.get("q") || "");
  const [r, setR] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      if (q.trim().length < 2) { setR(null); return; }
      setSp({ q }, { replace: true });
      api.get(`/groups/${gid}/search`, { params: { q } }).then((x) => setR(x.data));
    }, 250);
    return () => clearTimeout(t);
  }, [q, gid]); // eslint-disable-line react-hooks/exhaustive-deps

  const empty = r && !r.persons.length && !r.holdings.length && !r.transactions.length && !r.settlements.length && !r.demat_accounts.length;
  return (
    <div className="pb-6">
      <PageHeader title="Search" />
      <div className="relative">
        <SearchIcon size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#718096]" />
        <input autoFocus data-testid="search-input" className="hd-input pl-10" placeholder="Name, HS ID, stock, symbol, business, notes…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {r?.exact_person && (
        <Link to={`/person/${r.exact_person.person_id}`} className="hd-card mt-4 flex items-center gap-3 border-[#C8553D]/50" data-testid="search-exact-person">
          <Avatar name={r.exact_person.full_name} id={r.exact_person.person_id} size={44} />
          <div className="flex-1"><div className="font-bold">{r.exact_person.full_name}</div><div className="text-xs text-[#718096]">{r.exact_person.role === "karta" ? "Karta" : `Coparcener under ${r.exact_person.side_name}`}</div></div>
          <HsId value={r.exact_person.hs_id} />
        </Link>
      )}
      {empty && <div className="text-sm text-[#718096] mt-6 text-center" data-testid="search-no-results">No results for “{q}”</div>}
      {r?.persons.length > 0 && (
        <Section title="People">
          <div className="hd-card py-1" data-testid="search-persons">
            {r.persons.map((p) => (
              <Link key={p.person_id} to={`/person/${p.person_id}`} className="flex items-center gap-3 py-2.5 border-b border-[#E2D9CF]/50 last:border-0">
                <Avatar name={p.full_name} id={p.person_id} size={34} /><div className="flex-1 text-sm font-semibold">{p.full_name}<div className="text-[11px] text-[#718096] font-normal">{p.role === "karta" ? "Karta" : `Coparcener · ${p.side_name}`}</div></div><HsId value={p.hs_id} />
              </Link>
            ))}
          </div>
        </Section>
      )}
      {r?.holdings.length > 0 && (
        <Section title="Holdings">
          <div className="hd-card py-1" data-testid="search-holdings">
            {r.holdings.map((h) => (
              <Link key={h.id} to={`/holding/${h.id}`} className="block py-2.5 border-b border-[#E2D9CF]/50 last:border-0">
                <div className="text-sm font-semibold">{h.name}</div><div className="text-xs text-[#718096]">{CATEGORY_LABEL[h.category]}{h.symbol ? ` · ${h.symbol}` : ""}{h.legal_entity_name ? ` · ${h.legal_entity_name}` : ""}</div>
              </Link>
            ))}
          </div>
        </Section>
      )}
      {r?.transactions.length > 0 && <Section title="Transactions"><div className="hd-card py-1" data-testid="search-transactions">{r.transactions.map((t) => <TxnRow key={t.id} t={t} />)}</div></Section>}
      {r?.settlements.length > 0 && <Section title="Settlements"><div className="hd-card py-1" data-testid="search-settlements">{r.settlements.map((s) => <SettlementRow key={s.id} s={s} />)}</div></Section>}
      {r?.demat_accounts.length > 0 && (
        <Section title="Demat accounts"><div className="hd-card py-1">{r.demat_accounts.map((d) => <div key={d.id} className="py-2.5 text-sm"><b>{d.account_name}</b> · {d.broker}</div>)}</div></Section>
      )}
    </div>
  );
}
